import Stack from '@mui/material/Stack';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { BusinessUnit } from 'services/types/businessUnit';
import type { Client } from 'services/types/client';
import type { Lead } from 'services/types/lead';
import type { User } from 'services/types/user';

import ClientIdentityHero from './components/ClientIdentityHero';
import ClientNotesCard from './components/ClientNotesCard';
import InfoSection from './components/InfoSection';
import { subscribeToSaleCreatePrefill } from './components/saleCreatePrefillBus';
import { getSectionConfigs, type SectionConfig } from './components/sectionConfigs';

interface ClientInfoPanelProps {
  client: Client;
  businessUnit?: BusinessUnit;
  currentUser?: User | null;
  clientLeads?: Lead[];
  isConversationActive?: boolean;
  isCompact?: boolean;
  /**
   * If provided, only the sections with these IDs will be rendered.
   * Use the virtual id `'main-info'` to render the identity hero card,
   * and `'client-notes-card'` to render the always-visible notes card (sellers only).
   */
  allowedSectionIds?: string[];
  /** When true, the panel does not create its own scroll container (parent should scroll). */
  disableRootScroll?: boolean;
  /** When true, accordion title headers are hidden for all sections. */
  hideSectionTitles?: boolean;
  /** When provided, an "edit" pencil button is rendered in the identity hero next to the client's name. */
  onEditClient?: () => void;
}

type BusinessUnitType = 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales' | 'default';

const HERO_VIRTUAL_ID = 'main-info';
const NOTES_CARD_VIRTUAL_ID = 'client-notes-card';

const ClientInfoPanel: React.FC<ClientInfoPanelProps> = ({
  client,
  businessUnit = undefined,
  currentUser = null,
  clientLeads = undefined,
  isConversationActive = undefined,
  isCompact = false,
  allowedSectionIds = undefined,
  disableRootScroll = false,
  hideSectionTitles = false,
  onEditClient = undefined,
}) => {
  const businessUnitType: BusinessUnitType = (businessUnit?.type as BusinessUnitType) || 'default';
  const sectionConfigs = getSectionConfigs();

  const [sectionStates, setSectionStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    return subscribeToSaleCreatePrefill((prefill) => {
      if (prefill.clientId !== client.id) return;
      setSectionStates((prev) => ({ ...prev, 'sales-info': true }));
    });
  }, [client.id]);

  const isSeller = currentUser?.role === 'seller' || currentUser?.role === 'sellerWithAssignment';

  const showHero = !allowedSectionIds || allowedSectionIds.includes(HERO_VIRTUAL_ID);
  const showNotesCard = isSeller && (!allowedSectionIds || allowedSectionIds.includes(NOTES_CARD_VIRTUAL_ID));

  const visibleSections = sectionConfigs.filter((section) => {
    if (allowedSectionIds && !allowedSectionIds.includes(section.id)) return false;

    const showForBusinessUnit = section.showForBusinessUnits.includes(businessUnitType);

    const showCondition = section.showCondition
      ? section.showCondition(client, currentUser, clientLeads, businessUnit, isConversationActive)
      : true;

    return showForBusinessUnit && showCondition;
  });

  const handleSectionChange = (sectionId: string, expanded: boolean) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: expanded,
    }));
  };

  const isSectionExpanded = (section: SectionConfig): boolean => {
    if (sectionStates[section.id] !== undefined) {
      return sectionStates[section.id];
    }
    return section.defaultExpanded || false;
  };

  return (
    <Stack
      spacing={0.5}
      sx={{
        width: '100%',
        scrollbarWidth: 'none',
        p: 1,
        pl: 0.5,
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...(disableRootScroll ? { overflow: 'visible', maxHeight: 'none' } : { maxHeight: '100%', overflow: 'auto' }),
      }}
    >
      {showHero && <ClientIdentityHero client={client} businessUnit={businessUnit} onEdit={onEditClient} />}
      {showNotesCard && <ClientNotesCard client={client} />}
      {visibleSections.map((section) => (
        <InfoSection
          key={section.id}
          title={section.title}
          icon={section.icon}
          defaultExpanded={section.defaultExpanded}
          lazy={section.lazy}
          variant={isCompact ? 'compact' : 'default'}
          expanded={isSectionExpanded(section)}
          onChange={(expanded) => handleSectionChange(section.id, expanded)}
          hideSummary={hideSectionTitles}
        >
          {section.renderContent(client, businessUnit, currentUser, clientLeads, isConversationActive)}
        </InfoSection>
      ))}
    </Stack>
  );
};

export default ClientInfoPanel;
